import React from 'react'
import { Handle, NodeProps, Position } from '@xyflow/react'
import { SIGNAL_TYPE_COLORS } from '@/constants/colors'
import type { ZoneConnectionPoint } from '@busswerks/shared'
import type { SignalType } from '@/types/devices'

/**
 * A "wall" is a zone boundary marker that only renders when the
 * user is drilled into a zone. Every zone implicitly has two walls:
 *
 *   - Input wall  — accepts signal from outside the zone. Its
 *                    handles correspond to ZoneConnectionPoints
 *                    whose `direction` is 'input'. React Flow sees
 *                    each as a `target` handle (signal arrives).
 *   - Output wall — sends signal out of the zone. Handles are
 *                    ZoneConnectionPoints with `direction` of
 *                    'output', wired as `source` handles.
 *
 * In addition to the exposed-port handles, each wall carries a
 * single "drop target" handle (`drop-port`). Dropping a connection
 * there tells EditorClient to promote the dragged device port into
 * a new ZoneConnectionPoint rather than create an actual
 * device-to-device Connection. See EditorClient.handleConnect for
 * the detection.
 *
 * Walls are pure UI — they don't live in the store. EditorClient
 * synthesises them on the fly from the focused zone's
 * connectionPoints array each render.
 */
export interface WallNodeData {
  wallKind: 'input' | 'output'
  zoneId: string
  zoneName: string
  zoneColor: string
  // ZoneConnectionPoints whose direction matches this wall's kind.
  // Rendered as real handles the user can wire from/to.
  connectionPoints: ZoneConnectionPoint[]
  // Display labels keyed by ZoneConnectionPoint id. Computed from
  // the live device list at projection time so the wall always
  // shows "<device name> · <port name>" even for connection points
  // whose stored `name` is just the port name (the case when the
  // cp was created by dragging onto the wall).
  labels?: Record<string, { deviceName: string; portName: string }>
}

export const WALL_INPUT_NODE_PREFIX = 'wall-in-'
export const WALL_OUTPUT_NODE_PREFIX = 'wall-out-'
export const WALL_DROP_HANDLE_ID = 'drop-port'
// Suffix appended to a ZoneConnectionPoint id to form the handle
// id on the *outside* edge of a wall row — the port that faces
// peer zones rather than internal devices. Edges from the wall to
// a peer satellite terminate on this external handle; exposure
// edges from devices to the wall keep using the bare cp id on
// the inside edge.
export const EXT_HANDLE_SUFFIX = '::ext'

export function stripExtHandleSuffix(handleId: string): {
  cpId: string
  isExt: boolean
} {
  if (handleId.endsWith(EXT_HANDLE_SUFFIX)) {
    return {
      cpId: handleId.slice(0, -EXT_HANDLE_SUFFIX.length),
      isExt: true,
    }
  }
  return { cpId: handleId, isExt: false }
}

export function isWallNodeId(nodeId: string): boolean {
  return (
    nodeId.startsWith(WALL_INPUT_NODE_PREFIX) ||
    nodeId.startsWith(WALL_OUTPUT_NODE_PREFIX)
  )
}

export function wallKindFromNodeId(nodeId: string): 'input' | 'output' | null {
  if (nodeId.startsWith(WALL_INPUT_NODE_PREFIX)) return 'input'
  if (nodeId.startsWith(WALL_OUTPUT_NODE_PREFIX)) return 'output'
  return null
}

const WallNode: React.FC<NodeProps> = ({ data }) => {
  const wallData = data as unknown as WallNodeData
  const isInput = wallData.wallKind === 'input'

  // React Flow pairs handles as source → target (strict mode). In
  // DeviceNode, input ports are declared `target` and output ports
  // are `source`. For a wall handle to pair with a device port,
  // it has to be the opposite type:
  //
  //   - Input wall  ↔ device input ports (target)  → wall is source
  //   - Output wall ↔ device output ports (source) → wall is target
  //
  // Getting this wrong means drags won't snap or, worse, snap the
  // wrong way (which is what shipped last time — the user was only
  // able to attach inputs to the output wall, because that was the
  // only direction that matched).
  const handleType: 'source' | 'target' = isInput ? 'source' : 'target'

  // Walls flank the zone's device bounding box: the input wall sits
  // to the LEFT of the devices, so its handles need to face RIGHT
  // (into the zone). The output wall sits to the RIGHT and its
  // handles face LEFT. Otherwise the drop targets render on the
  // outside edge and the user can't reach them from device ports.
  const handlePosition = isInput ? Position.Right : Position.Left

  const color = wallData.zoneColor || '#94a3b8'

  return (
    <div
      className="relative select-none"
      style={{ width: 240, minHeight: 360 }}
      title={
        isInput
          ? 'Zone input wall — drag device input ports here to expose them on the zone boundary'
          : 'Zone output wall — drag device output ports here to expose them on the zone boundary'
      }
    >
      <div
        className="h-full rounded-xl border-2 border-dashed px-3 py-4 flex flex-col"
        style={{
          backgroundColor: color + '10',
          borderColor: color + 'aa',
          minHeight: 360,
        }}
      >
        <div
          className="text-[10px] font-bold uppercase tracking-widest text-center"
          style={{ color }}
        >
          {isInput ? 'Zone Inputs' : 'Zone Outputs'}
        </div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 text-center mt-0.5 mb-3">
          {wallData.zoneName}
        </div>

        {wallData.connectionPoints.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-[10px] italic text-gray-500 dark:text-gray-400 text-center px-2">
              Drag a device {isInput ? 'input' : 'output'} port here to expose
              it on the zone boundary.
            </p>
          </div>
        ) : (
          <ul className="space-y-1.5 flex-1">
            {wallData.connectionPoints.map(cp => {
              const portColor =
                SIGNAL_TYPE_COLORS[cp.signalType as SignalType] ?? '#999'
              const label = wallData.labels?.[cp.id]
              const deviceName = label?.deviceName
              const portName = label?.portName ?? cp.name
              return (
                <li
                  key={cp.id}
                  // `relative` is the anchor for the Handle child
                  // below. The Handle tracks the row's vertical
                  // center (top:50%) so it always stays aligned
                  // with its list item — even as rows grow to fit
                  // longer labels or wrap. Previously handles were
                  // absolute-positioned siblings of the list with
                  // a static spacing constant, which drifted out
                  // of alignment as the list grew.
                  className="relative flex items-center gap-2 bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1.5"
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: portColor }}
                  />
                  <div className="min-w-0 flex-1 flex items-baseline gap-1.5">
                    {deviceName && (
                      <span
                        className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate flex-shrink-0 max-w-[55%]"
                        title={deviceName}
                      >
                        {deviceName}
                      </span>
                    )}
                    <span
                      className="text-[10px] text-gray-700 dark:text-gray-200 truncate"
                      title={portName}
                    >
                      {portName}
                    </span>
                  </div>
                  {/*
                    Internal handle (inside edge of the wall). Pairs
                    with device ports via exposure edges. `handleType`
                    is already picked to match the device side:
                    - Input wall internal = source (pairs with device
                      input target)
                    - Output wall internal = target (pairs with
                      device output source)
                  */}
                  <Handle
                    id={cp.id}
                    type={handleType}
                    position={handlePosition}
                    style={{
                      background: portColor,
                      width: 12,
                      height: 12,
                      border: '2px solid #0f172a',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      [isInput ? 'right' : 'left']: -14,
                      [isInput ? 'left' : 'right']: 'auto',
                    }}
                    title={`${cp.name} internal (${cp.signalType})`}
                  />
                  {/*
                    External handle (outside edge of the wall). This
                    is the zone's external-facing port — what the
                    user drags to a peer satellite handle to create
                    a cross-zone link. Type is opposite the internal
                    handle so it pairs with the mirror-typed peer
                    satellite handles:
                    - Input wall external = target (pairs with
                      peer-out satellite source handles)
                    - Output wall external = source (pairs with
                      peer-in satellite target handles)
                    Position is flipped to the outside-facing side.
                  */}
                  <Handle
                    id={`${cp.id}${EXT_HANDLE_SUFFIX}`}
                    type={handleType === 'source' ? 'target' : 'source'}
                    position={isInput ? Position.Left : Position.Right}
                    style={{
                      background: portColor,
                      width: 12,
                      height: 12,
                      border: '2px solid #0f172a',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      [isInput ? 'left' : 'right']: -14,
                      [isInput ? 'right' : 'left']: 'auto',
                    }}
                    title={`${cp.name} external (${cp.signalType})`}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Drop-target handle that catches ALL drags ending on this
          wall. EditorClient's handleConnect checks for this id and
          promotes the dragged device port to a new
          ZoneConnectionPoint instead of creating an actual
          connection. Spans the full inside-facing edge so a drag
          released anywhere along that edge counts as a drop. */}
      <Handle
        id={WALL_DROP_HANDLE_ID}
        type={handleType}
        position={handlePosition}
        style={{
          background: color,
          width: 16,
          height: '80%',
          borderRadius: 6,
          border: '2px dashed ' + color,
          opacity: 0.35,
          top: '10%',
          bottom: '10%',
          // Pin to the inside-facing edge of the wall bar.
          [isInput ? 'right' : 'left']: 0,
          [isInput ? 'left' : 'right']: 'auto',
          transform: 'none',
        }}
        title={`Drop any device ${isInput ? 'input' : 'output'} port here to expose it`}
      />
    </div>
  )
}

export default WallNode
